import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { categories } from '@/mocks/categories';
import { getHotVideos, getNewVideos, videos } from '@/mocks/videos';
import { faqList } from '@/mocks/faq';
import type { Video } from '@/mocks/videos';

function HomeSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '17c·moc起草糖心在线看',
    url: 'https://17c-moc.com',
    description: '17c·moc起草糖心在线看提供海量高清视频资源，无需下载，随时随地畅享在线观看体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://17c-moc.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function FaqSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function StatsSection() {
  const stats = [
    { value: '50,000+', label: '视频资源', icon: 'ri-film-line' },
    { value: '1,200万+', label: '月活跃用户', icon: 'ri-user-line' },
    { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
    { value: '4K', label: '超高清画质', icon: 'ri-4k-line' },
  ];

  return (
    <section className="py-14 bg-background-100">
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-xl flex items-center justify-center">
                <i className={`${stat.icon} text-primary-500 text-xl`} style={{ width: '24px', height: '24px' }}></i>
              </div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {stat.value}
              </div>
              <div className="mt-1 text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [hotVideos, setHotVideos] = useState<Video[]>([]);
  const [newVideos, setNewVideos] = useState<Video[]>([]);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setHotVideos(getHotVideos());
    setNewVideos(getNewVideos());
  }, []);

  return (
    <>
      <HomeSchema />
      <FaqSchema />

      <main>
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20composition%20with%20flowing%20gradient%20waves%20in%20warm%20coral%20and%20deep%20charcoal%20tones%2C%20geometric%20shapes%20floating%2C%20dark%20dramatic%20atmosphere%20with%20accent%20light%20streaks%2C%20modern%20minimalist%20digital%20art%2C%20sleek%20entertainment%20platform%20hero%20background%2C%20no%20text%2C%20clean%20composition&width=1800&height=900&seq=hero-bg-2026&orientation=landscape"
            alt="17c·moc起草糖心在线看 - 免费高清视频平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>

          <div className="relative z-10 container-custom text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></div>
              <span className="text-white/90 text-sm">最新内容今日已更新</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl mx-auto">
              免费高清视频平台
              <br />
              <span className="text-accent-400">随时随地在线看</span>
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              糖心在线看免费入口，海量高清视频资源，无需下载，多终端兼容，畅享流畅在线观看体验
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  navigate('/category/dianying');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="px-7 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors text-base whitespace-nowrap cursor-pointer"
              >
                立即观看
              </button>
              <Link
                to="/about"
                className="px-7 py-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-medium rounded-full hover:bg-white/20 transition-colors text-base whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <Navbar />

        <section className="py-14">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  热门推荐
                </h2>
                <p className="mt-1 text-foreground-500 text-sm">大家都在看的热门视频，不容错过</p>
              </div>
              <Link
                to="/category/dianying"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" style={{ width: '14px', height: '14px' }}></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotVideos.slice(0, 8).map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  视频分类
                </h2>
                <p className="mt-1 text-foreground-500 text-sm">八大分类，满足你的所有观影需求</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group bg-white rounded-xl p-5 border border-background-200/70 hover:border-background-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-primary-500 text-xl`} style={{ width: '24px', height: '24px' }}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1.5">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <StatsSection />

        <section className="py-14">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="mt-1 text-foreground-500 text-sm">新鲜上线的精彩内容，第一时间呈现</p>
              </div>
              <Link
                to="/category/dianying"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" style={{ width: '14px', height: '14px' }}></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {newVideos.slice(0, 8).map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="container-custom">
            <div className="flex items-center gap-4 mb-8">
              <CategoryTabs />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {videos.slice(0, 8).map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="py-14">
          <div className="container-custom max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="mt-2 text-foreground-500 text-sm">关于糖心在线看，你可能想了解的都在这里</p>
            </div>
            <div className="space-y-3">
              {faqList.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <span className="font-heading font-semibold text-foreground-900 pr-4 text-sm md:text-base">
                      {faq.question}
                    </span>
                    <div
                      className={`w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-300 ${
                        openFaq === faq.id ? 'rotate-180' : ''
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-foreground-500" style={{ width: '18px', height: '18px' }}></i>
                    </div>
                  </button>
                  {openFaq === faq.id && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}